import { useEffect, useRef, useState } from "react";
import { normalizeNumericDraft, registerInputDraft } from './input-drafts';
import type { JSX, ReactNode } from "react";
import { Button, Input, Label, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, PopoverTrigger, Tooltip } from "@fluentui/react-components";
import { AdaptivePopover, AdaptivePopoverSurface } from './adaptive-popover';
import { useCompactScreen } from './session-state';
import { CheckmarkRegular, DismissRegular, FilterRegular, SearchRegular, SettingsRegular } from "@fluentui/react-icons";
import { closestCenter, DndContext, type DragEndEvent, MouseSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { rectSortingStrategy, SortableContext, useSortable, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { MAX_SCORE_PRECISION_DIGITS } from "@minglun/domain";
import type { ResourceGroup } from "@minglun/domain";
import { moveIdWithinList } from "../ordering";
import { formatScorePrecision, parseScorePrecision } from "../scientific-precision";
import { type Page, resourceGroupLabels, type FilterOption } from "../workbench-shared";

export function NavButton({
  item,
  active,
  onClick,
}: {
  item: { id: Page; label: string; description?: string };
  active: boolean;
  onClick(): void;
}): JSX.Element {
  return (
    <button
      type="button"
      className={`nav-button ${active ? "active" : ""}`}
      aria-current={active ? "page" : undefined}
      onClick={onClick}
      title={item.description ?? item.label}
    >
      {item.label}
    </button>
  );
}

export function TagPills({
  names,
  compact = false,
}: {
  names: string[];
  compact?: boolean;
}): JSX.Element {
  return (
    <span className={`tag-pills ${compact ? "compact" : ""}`}>
      {names.length ? (
        names.map((name) => (
          <span className="tag-pill" key={name}>
            {name}
          </span>
        ))
      ) : (
        <span className="tag-pill empty">未分类</span>
      )}
    </span>
  );
}

export function ActionCluster({ children }: { children: ReactNode }): JSX.Element {
  return <div className="action-cluster">{children}</div>;
}

export function GroupBadge({
  group,
  compact = false,
}: {
  group: ResourceGroup;
  compact?: boolean;
}): JSX.Element {
  return (
    <span
      className={`group-badge group-${group.toLowerCase().replace("_", "-")} ${compact ? "compact" : ""}`}
    >
      {resourceGroupLabels[group]}
    </span>
  );
}

export function SearchBox({
  value,
  onChange,
  placeholder,
  resultCount,
  totalCount,
}: {
  value: string;
  onChange(value: string): void;
  placeholder: string;
  resultCount?: number;
  totalCount?: number;
}): JSX.Element {
  const countLabel =
    resultCount === undefined
      ? undefined
      : `${resultCount}${totalCount === undefined ? "" : ` / ${totalCount}`} 项`;
  return (
    <div className="search-box">
      <Tooltip
        content="支持空格组合、双引号短语、-排除和“字段:值”"
        relationship="label"
      >
        <Input
          className="search-input"
          data-search-input="true"
          contentBefore={<SearchRegular />}
          contentAfter={<>
            {countLabel && <output className="search-count" aria-live="polite">{countLabel}</output>}
            {value && <button type="button" className="search-clear" aria-label="清除搜索" title="清除搜索" onMouseDown={event => event.preventDefault()} onClick={() => onChange("")}><DismissRegular /></button>}
          </>}
          value={value}
          aria-label={`${placeholder}，支持高级搜索`}
          placeholder={placeholder}
          onChange={(_event, data) => onChange(data.value)}
        />
      </Tooltip>

    </div>
  );
}

export function FilterMenu({
  label,
  value,
  options,
  onChange,
  icon = <FilterRegular />,
  action = "筛选",
}: {
  label: string;
  value: string;
  options: FilterOption[];
  onChange(value: string): void;
  icon?: ReactNode;
  action?: string;
}): JSX.Element {
  const selected =
    options.find((option) => option.value === value) ?? options[0];
  const isActive = selected ? selected.value !== options[0]?.value : false;
  const compact = useCompactScreen();
  const [open, setOpen] = useState(false);
  if (compact) return <AdaptivePopover open={open} onOpenChange={(_event, data) => setOpen(data.open)} positioning="below-start">
    <PopoverTrigger disableButtonEnhancement><button type="button" className={`filter-menu-button ${isActive ? 'active' : ''}`} aria-label={`${label}：${selected?.label ?? value}`} aria-expanded={open}>
      <span className="filter-menu-icon">{icon}</span><span>{label}</span><strong>{selected?.label ?? value}</strong>
    </button></PopoverTrigger>
    <AdaptivePopoverSurface aria-label={label}><div className="compact-filter-options" role="menu">
      {options.map(option => <button type="button" role="menuitemradio" aria-checked={option.value === value} key={option.value} onClick={() => { onChange(option.value); setOpen(false); }}><CheckmarkRegular style={{ visibility: option.value === value ? 'visible' : 'hidden' }} />{option.label}</button>)}
    </div></AdaptivePopoverSurface>
  </AdaptivePopover>;
  return (
    <Menu positioning="below-start">
      <MenuTrigger disableButtonEnhancement>
        <button
          type="button"
          className={`filter-menu-button ${isActive ? "active" : ""}`}
          aria-label={`${label}：${selected?.label ?? value}`}
          title={`${action}${label}`}
        >
          <span className="filter-menu-icon">{icon}</span>
          <span className="filter-menu-label">{label}</span>
          <strong>{selected?.label ?? value}</strong>
        </button>
      </MenuTrigger>
      <MenuPopover>
        <MenuList aria-label={label}>
          {options.map((option) => (
            <MenuItem
              key={option.value}
              icon={
                <CheckmarkRegular
                  className={
                    option.value === value
                      ? "filter-option-selected"
                      : "filter-option-empty"
                  }
                />
              }
              onClick={() => onChange(option.value)}
            >
              {option.label}
            </MenuItem>
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

export function NumberInput({
  value,
  onChange,
  min,
  max,
  step = 1,
  ariaLabel,
  disabled = false,
  suffix,
}: {
  value: number;
  onChange(value: number): void;
  min?: number;
  max?: number;
  step?: number;
  ariaLabel?: string;
  disabled?: boolean;
  suffix?: string;
}): JSX.Element {
  const [text, setText] = useState(String(value));
  const draft = useRef(String(value));
  const changed = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const unregister = useRef<(() => void) | undefined>(undefined);
  const latest = useRef({ value, min, max, step, onChange });
  latest.current = { value, min, max, step, onChange };
  useEffect(() => { if (!changed.current) { setText(String(value)); draft.current = String(value); } }, [value]);
  useEffect(() => () => { clearTimeout(timer.current); unregister.current?.(); }, []);
  const commit = (): void => {
    clearTimeout(timer.current);
    unregister.current?.(); unregister.current = undefined;
    if (!changed.current) return;
    changed.current = false;
    const state = latest.current;
    const next = normalizeNumericDraft(draft.current, state.value, state.min, state.max, state.step);
    draft.current = String(next); setText(String(next));
    if (next !== state.value) state.onChange(next);
  };
  return (
    <Input
      type="number"
      inputMode={step === 1 ? 'numeric' : 'decimal'}
      value={text}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      aria-label={ariaLabel}
      contentAfter={
        suffix ? <span className="number-input-suffix">{suffix}</span> : null
      }
      onChange={(_event, data) => {
        setText(data.value);
        draft.current = data.value; changed.current = true;
        clearTimeout(timer.current);
        unregister.current?.(); unregister.current = registerInputDraft(commit);
        if (data.value.trim() && Number.isFinite(Number(data.value))) timer.current = setTimeout(commit, 250);
      }}
      onBlur={commit}
      onFocus={(event) => event.currentTarget.select()}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.currentTarget.blur();
        if (event.key === "Escape") {
          changed.current = false; clearTimeout(timer.current); unregister.current?.();
          draft.current = String(value);
          setText(String(value));
          event.currentTarget.blur();
        }
      }}
    />
  );
}

export function ScientificPrecisionInput({
  value,
  onChange,
}: {
  value: number;
  onChange(value: number): void;
}): JSX.Element {
  const canonical = formatScorePrecision(value);
  const [text, setText] = useState(canonical);
  const draft = useRef(canonical);
  const changed = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const unregister = useRef<(() => void) | undefined>(undefined);
  const latest = useRef({ value, canonical, onChange });
  latest.current = { value, canonical, onChange };
  useEffect(() => { if (!changed.current) { setText(canonical); draft.current = canonical; } }, [canonical]);
  useEffect(() => () => { clearTimeout(timer.current); unregister.current?.(); }, []);
  const parsed = parseScorePrecision(text);
  const invalid = text.trim().length > 0 && parsed === undefined;
  const commit = (): void => {
    clearTimeout(timer.current);
    unregister.current?.(); unregister.current = undefined;
    if (!changed.current) return;
    changed.current = false;
    const state = latest.current;
    const next = parseScorePrecision(draft.current);
    draft.current = next === undefined ? state.canonical : formatScorePrecision(next);
    setText(draft.current);
    if (next !== undefined && next !== state.value) state.onChange(next);
  };
  return (
    <Input
      className={`scientific-precision-input ${invalid ? "is-invalid" : ""}`}
      type="text"
      inputMode="text"
      value={text}
      maxLength={5}
      placeholder="1e-5"
      aria-label={`评分精度，输入 1e-1 到 1e-${MAX_SCORE_PRECISION_DIGITS}`}
      aria-invalid={invalid}
      title={`格式：1e-1 至 1e-${MAX_SCORE_PRECISION_DIGITS}`}
      onChange={(_event, data) => {
        const next = data.value;
        if (!/^[0-9eE-]*$/.test(next)) return;
        setText(next);
        draft.current = next; changed.current = true;
        clearTimeout(timer.current);
        unregister.current?.(); unregister.current = registerInputDraft(commit);
        if (parseScorePrecision(next) !== undefined) timer.current = setTimeout(commit, 250);
      }}
      onBlur={commit}
      onFocus={(event) => event.currentTarget.select()}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.currentTarget.blur();
        if (event.key === "Escape") {
          changed.current = false; clearTimeout(timer.current); unregister.current?.();
          draft.current = canonical;
          setText(canonical);
          event.currentTarget.blur();
        }
      }}
    />
  );
}

export function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <Label className="field">
      <span>{label}</span>
      {children}
    </Label>
  );
}

export function Rarity({ rarity }: { rarity: string }): JSX.Element {
  return (
    <span className={`rarity rarity-${rarity.toLowerCase()}`}>{rarity}</span>
  );
}

export function EmptyState({
  label,
  detail,
}: {
  label: string;
  detail?: string;
}): JSX.Element {
  return (
    <div className="empty-state" role="status">
      <SettingsRegular />
      <strong>{label}</strong>
      {detail && <span>{detail}</span>}
    </div>
  );
}

export function NoResults({
  label,
  query,
  onClear,
}: {
  label: string;
  query?: string;
  onClear(): void;
}): JSX.Element {
  return (
    <div className="no-results" role="status">
      <SearchRegular />
      <div>
        <strong>{label}</strong>
        {query && <small>“{query}”没有匹配项</small>}
      </div>
      <Button appearance="subtle" onClick={onClear}>
        重置筛选
      </Button>
    </div>
  );
}

export function SortableList({
  ids,
  onReorder,
  children,
}: {
  ids: string[];
  onReorder(ids: string[]): void;
  children: ReactNode;
}): JSX.Element {
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    // A swipe scrolls; only an intentional long press starts touch sorting.
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEnd = (event: DragEndEvent): void => {
    const active = String(event.active.id);
    const over = event.over ? String(event.over.id) : undefined;
    if (!over || active === over) return;
    const reordered = moveIdWithinList(ids, active, over);
    if (reordered !== ids) onReorder(reordered);
  };
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={ids} strategy={rectSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
}

export function SortableEntity({
  id,
  children,
  disabled = false,
}: {
  id: string;
  children: ReactNode;
  disabled?: boolean;
}): JSX.Element {
  const sortable = useSortable({ id, disabled });
  return (
    <div
      ref={sortable.setNodeRef}
      className={disabled ? "sortable-disabled" : "sortable-enabled"}
      style={{
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
      }}
      {...(disabled ? {} : sortable.attributes)}
      {...(disabled ? {} : sortable.listeners)}
    >
      {children}
    </div>
  );
}

